import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { ReplaySubject, tap } from 'rxjs';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { ACCESS_TOKEN } from '../../storage/constants.lib';
import { DiagnosisEventQuery } from '../../models/diagnosis-event-query';
import { DiagnosisEventFilterValues } from '../../models/diagnosis-event-filter-values';
import { PayerConfigQuery } from '../../models/payer-config-query';
import { PayerConfigFilterValues } from '../../models/payer-config-filter-values';
import { MatDialog } from '@angular/material/dialog';
import { ServiceFormatter } from '../../helpers/service-formatter';
import { OkDialogComponent, OkDialogModel } from '../../dialogs/ok-dialog/ok-dialog.component';
import { AuditLogQuery } from '../../models/grid-query';
import { AsmFileUpdateRequest } from '../../models/asm-file-update-request';
import { FileDownloadHelper } from '../../helpers/file-download-helper';

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  private endpoint = `${environment.baseURL}/api/`;

  exportInProgress: ReplaySubject<boolean>;

  constructor(private http: HttpClient, private dialog: MatDialog) { 
    this.exportInProgress = new ReplaySubject<boolean>(1);
    this.exportInProgress.next(false);
  }

  exportDiagnosisEvents(query: DiagnosisEventQuery, sourceComp: string, docType: string, templateID: number, filterValues: DiagnosisEventFilterValues, asmRequest?: AsmFileUpdateRequest, serviceYear?: number | null | undefined) {

    return this.http.post<any>(this.endpoint + "diagnosisevent/export", {
      Query: query,
      Source: sourceComp,
      DocType: docType,
      FilterValues: filterValues,
      TemplateID: templateID,
      AsmFileUpdateRequest: asmRequest,
      ServiceYear: serviceYear,
    }, ServiceFormatter.getHttpOptions());
  }

  exportAuditLogs(query: AuditLogQuery) {

    return this.http.post<any>(this.endpoint + "log/export", query, ServiceFormatter.getHttpOptions())
    .subscribe(() => {
      const message = [
        `Requested records are being exported and will be available for download from the My Exports page within the User menu.`,
        `Dependent upon the data request, it may be several minutes before the export is available.`
      ];

      this.dialog.open(OkDialogComponent, new OkDialogModel(
        'Export Requested',
        message
      ));
    });
  }

  exportPayerConfig(query: PayerConfigQuery, filterValues: PayerConfigFilterValues) {

    this.exportInProgress.next(true);

    const token = localStorage.getItem(ACCESS_TOKEN);
    let headers = new HttpHeaders();
    headers = headers.append("Authorization", "Bearer " + token);

    return this.http.post<any>(this.endpoint + "payer/export", {
      Query: query,
      FilterValues: filterValues
    }, 
    {
      headers: headers
    }
    ).subscribe(result => {
      if(!result) {
        return;
      }

      FileDownloadHelper.downloadBase64As(result.FileContents, result.FileDownloadName);

    }, error => {
      this.exportInProgress.next(false);
    },
    () => {
      this.exportInProgress.next(false);
    });
  }

  uploadReportBlob(blob: Blob, reportName: string, recordCount: number, extension: string = 'xlsx') {
    const formData = new FormData();
    formData.append('file', blob, `${reportName}.${extension}`);
    formData.append('recordCount', recordCount.toString());
    return this.http.post(this.endpoint + "reports/upload", formData, {
      ...ServiceFormatter.getHttpOptions(),
      responseType: 'text'
    });
  }

  uploadThenDownloadReport(blob: Blob, reportName: string, recordCount: number, extension: string = 'xlsx') {
    return this.uploadReportBlob(blob, reportName, recordCount, extension).pipe(
      tap(filename => FileDownloadHelper.downloadBlobAs(blob, filename))
    );
  }
}
